import '../scss/components/_ownerreports.scss'
import React, { useState, useEffect, useRef } from 'react'
import OwnerOccupancyReport from './OwnerOccupancyReport'
import OwnerRevenueReport from './OwnerRevenueReport'
import OwnerComplaintsReport from './OwnerComplaintsReport'
import OwnerComplianceReport from './OwnerComplianceReport'

// ─── Constants ───────────────────────────────────────────────────────────────

const REPORT_TYPES = [
  { key: 'occupancy',  label: 'Occupancy Report'  },
  { key: 'revenue',    label: 'Revenue Report'     },
  { key: 'complaints', label: 'Complaints Report'  },
  { key: 'compliance', label: 'Compliance Report'  },
]

const MOCK_MONTHLY = {
  2023: [74, 71, 77, 80, 76, 82, 87, 85, 83, 80, 77, 75],
  2024: [80, 83, 86, 89, 85, 90, 93, 91, 88, 87, 83, 81],
  2025: [84, 87, 90, 92, 89, 94, 97, 95, 92, 90, 86, 85],
  2026: [86, 88, 91, 93, 90, 95, null, null, null, null, null, null],
}

// ─── Root Component ──────────────────────────────────────────────────────────

const OwnerReports = ({ selectedPG, selectedPGId }) => {
  const today    = new Date()
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
  const fmt      = d => d.toISOString().split('T')[0]

  const [dateFrom,      setDateFrom]    = useState(fmt(firstDay))
  const [dateTo,        setDateTo]      = useState(fmt(today))
  const [activeReport,  setActiveReport] = useState('occupancy')
  const [dropOpen,      setDropOpen]    = useState(false)
  const dropRef = useRef(null)

  const currentYear    = today.getFullYear()
  const availableYears = Object.keys(MOCK_MONTHLY).map(Number).sort((a, b) => a - b)
  const defaultYear    = availableYears.includes(currentYear) ? currentYear : availableYears[availableYears.length - 1]
  const [selectedYear, setSelectedYear] = useState(defaultYear)

  useEffect(() => {
    const handler = e => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const activeLabel = REPORT_TYPES.find(r => r.key === activeReport)?.label || 'Select Report'

  return (
    <div className="rpt-root">

      {/* ── Top bar ── */}
      <div className="rpt-topbar">
        <div className="rpt-topbar-left">
          <div className="rpt-date-group">
            <label className="rpt-date-label">From</label>
            <input type="date" className="rpt-date-input" value={dateFrom} max={dateTo} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <span className="rpt-date-sep">→</span>
          <div className="rpt-date-group">
            <label className="rpt-date-label">To</label>
            <input type="date" className="rpt-date-input" value={dateTo} min={dateFrom} max={fmt(today)} onChange={e => setDateTo(e.target.value)} />
          </div>
        </div>

        <div className="rpt-dropdown" ref={dropRef}>
          <button className="rpt-dropdown-btn" onClick={() => setDropOpen(o => !o)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 9h6M9 12h6M9 15h4" />
            </svg>
            {activeLabel}
            <svg className={`rpt-chevron ${dropOpen ? 'rpt-chevron-open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {dropOpen && (
            <div className="rpt-dropdown-menu">
              {REPORT_TYPES.map(r => (
                <button
                  key={r.key}
                  className={`rpt-dropdown-item ${activeReport === r.key ? 'rpt-dropdown-item-active' : ''}`}
                  onClick={() => { setActiveReport(r.key); setDropOpen(false) }}
                >
                  {r.label}
                  {activeReport === r.key && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Header + year pills ── */}
      <div className="rpt-header">
        <div>
          <h2 className="rpt-title">{activeLabel}</h2>
          <p className="rpt-subtitle">
            {selectedPG} · {new Date(dateFrom).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })} – {new Date(dateTo).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
          </p>
        </div>
        <div className="rpt-year-filter">
          {availableYears.slice(-3).map(year => (
            <button key={year} className={`rpt-year-btn ${selectedYear === year ? 'active' : ''}`} onClick={() => setSelectedYear(year)}>
              {year}
            </button>
          ))}
        </div>
      </div>

      {/* ── Active report ── */}
      {activeReport === 'occupancy' && (
        <OwnerOccupancyReport
          selectedPGId={selectedPGId}
          selectedPG={selectedPG}
          dateFrom={dateFrom}
          dateTo={dateTo}
          selectedYear={selectedYear}
          mockMonthlyData={MOCK_MONTHLY}
        />
      )}
      {activeReport === 'complaints' && (
        <OwnerComplaintsReport
          selectedPGId={selectedPGId}
          selectedPG={selectedPG}
          selectedYear={String(selectedYear)}
        />
      )}
      {activeReport === 'revenue' && <OwnerRevenueReport selectedYear={selectedYear} />}
      {activeReport === 'compliance' && <OwnerComplianceReport />}

    </div>
  )
}

export default OwnerReports